import type { BetterAuthClientPlugin } from "better-auth/client";
import { customSessionClient } from "better-auth/client/plugins";
import { createAuthClient } from "better-auth/react";
import type { auth } from "../../../../apps/api/dist/src/lib/auth";

export interface AuthClientConfig<P extends BetterAuthClientPlugin[] = []> {
	/** API base URL (e.g., import.meta.env.VITE_API_URL) */
	apiUrl: string;
	/** Additional Better Auth client plugins (e.g., passkeyClient, emailOTPClient) */
	plugins?: P;
	/** Called when the session changes (sign-in, sign-out, sign-up) — use to invalidate CSRF token */
	onSessionChange?: () => void;
}

const AUTH_PATHS = ["/sign-in", "/sign-out", "/sign-up", "/callback"];

/**
 * Creates a configured Better Auth client for a Planvera frontend app.
 * Always includes `customSessionClient` for session extensions (activeOrganizationId, hasAcceptedConsents).
 */
export const createPlanveraAuthClient = <
	P extends BetterAuthClientPlugin[] = [],
>({
	apiUrl,
	plugins,
	onSessionChange,
}: AuthClientConfig<P>) => {
	return createAuthClient({
		baseURL: apiUrl,
		plugins: [
			customSessionClient<typeof auth>(),
			...(plugins ?? ([] as unknown as P)),
		],
		fetchOptions: onSessionChange
			? {
					onSuccess: (ctx) => {
						const url = ctx.response.url;
						if (AUTH_PATHS.some((path) => url.includes(path))) {
							onSessionChange();
						}
					},
				}
			: undefined,
	});
};
